/**
 * Meet & Assist Inquiry page (/:lng/services/meet-assist-inquiry).
 * Reached from the "Inquire Now" buttons on the services page's Meet & Assist tab.
 *
 * The guest picks Airport or Station, then Arrival / Departure / Connecting,
 * fills in the form, and sends it. The inquiry is emailed to the team -- no
 * booking is created and nothing is charged.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import SuccessModal from "../../../components/modal/SuccessModal";
import { DURATION, EASE_OUT, Reveal, Stagger, StaggerItem, SwapPanel } from "../../../components/motion";
import { createCancelSource, submitMeetAssistInquiry } from "./api";
import { CarDriverSection, ContactSection, PassengerInformationSection } from "./components/PeopleSections";
import { Fold } from "./components/animated";
import { CancellationPolicyLink, SubmitPanel, SummaryCard, TotalCard } from "./components/InquirySidebar";
import { FormCard, GroupTabs, JourneyTabs } from "./components/layout";
import { MeetAssistDetailsSection } from "./components/MeetAssistDetailsSection";
import {
  GROUP_AIRPORT,
  HERO_IMAGE_URL,
  JOURNEY_ARRIVAL,
  JOURNEYS_BY_GROUP,
  SERVICE_CONFIG,
  SERVICE_KEYS,
  getServiceKey,
} from "./constants";
import { useInquiryForm, useMeetAssistPrices } from "./hooks";
import {
  buildInquiryPayload,
  getPassengerCount,
  getSelectedTier,
  mapServerFieldErrors,
  validateInquiryForm,
} from "./inquiryForm";
import { calculateInquiryPrice } from "./pricing";

/**
 * Brings the first field with an error message into view after a failed
 * submit, and puts the cursor in it. Waits a frame so the messages exist.
 */
const scrollToFirstInvalidField = () => {
  requestAnimationFrame(() => {
    const firstErrorMessage = document.querySelector('[data-field-error="true"]');
    if (!firstErrorMessage) return;
    const fieldContainer = firstErrorMessage.parentElement || firstErrorMessage;
    fieldContainer.scrollIntoView({ block: "center", behavior: "smooth" });
    const firstControl = fieldContainer.querySelector("input, select, textarea, button");
    if (firstControl) firstControl.focus({ preventScroll: true });
  });
};

/** Tab order, used to slide the form the same way the guest moved. */
const SERVICE_ORDER = Object.values(SERVICE_KEYS);

/** 1 when the new service sits to the right of the old one, -1 when to the left. */
const useServiceSlideDirection = (serviceKey) => {
  const previousIndexRef = useRef(SERVICE_ORDER.indexOf(serviceKey));
  const currentIndex = SERVICE_ORDER.indexOf(serviceKey);
  const direction = currentIndex >= previousIndexRef.current ? 1 : -1;
  useEffect(() => {
    previousIndexRef.current = currentIndex;
  }, [currentIndex]);
  return direction;
};

/** The photo settles from a slight zoom while the title rises in. */
const HERO_IMAGE_ZOOM = 1.06;

const MeetAssistHero = () => {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  return (
    // The dark background shows if the photo is slow or fails to load.
    <div className="tw:relative tw:h-[240px] tw:lg:h-[312px] tw:overflow-hidden tw:bg-[#282828]">
      <motion.img
        src={HERO_IMAGE_URL}
        alt=""
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
        initial={{ scale: shouldReduceMotion ? 1 : HERO_IMAGE_ZOOM, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: DURATION.slow * 1.6, ease: EASE_OUT }}
        className="tw:absolute tw:inset-0 tw:size-full tw:object-cover tw:object-[60%_50%]"
      />
      <div className="tw:absolute tw:inset-0 tw:bg-[linear-gradient(76.3deg,#181818_0.7%,rgba(40,42,45,0.3)_33.7%)]" />
      <Stagger
        onMount
        delayChildren={0.15}
        stagger={0.12}
        className="tw:relative tw:h-full tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-y-3 tw:lg:gap-y-[14px] tw:px-5 tw:text-center"
      >
        <StaggerItem
          as="span"
          y={12}
          className="tw:font-hanken-grotesk tw:font-semibold tw:text-[8px] tw:lg:text-[10px] tw:leading-[12px] tw:tracking-[3px] tw:uppercase tw:text-[#D4B055]"
        >
          {t("meetAssistInquiry.Hero.Eyebrow")}
        </StaggerItem>
        <StaggerItem
          as="h1"
          y={18}
          className="tw:!mb-0 tw:font-libre-caslon-display tw:font-normal tw:text-[32px] tw:leading-[42px] tw:lg:text-[45px] tw:lg:leading-[58px] tw:text-white"
        >
          {t("meetAssistInquiry.Hero.Title")}
        </StaggerItem>
      </Stagger>
    </div>
  );
};

const MeetAssistInquiryPage = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language === "en" ? "en" : "jp";

  const [activeGroup, setActiveGroup] = useState(GROUP_AIRPORT);
  const [activeJourney, setActiveJourney] = useState(JOURNEY_ARRIVAL);
  const serviceKey = getServiceKey(activeGroup, activeJourney);
  const serviceConfig = SERVICE_CONFIG[serviceKey];
  const slideDirection = useServiceSlideDirection(serviceKey);

  const { form, updateField, resetForm, actions } = useInquiryForm();
  const sitePrices = useMeetAssistPrices();
  const selectedTier = getSelectedTier(form, serviceKey);

  // Errors stay hidden until the guest first presses Submit, then update live.
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [submitErrorKey, setSubmitErrorKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const submitCancelSourceRef = useRef(null);

  const clientFieldErrors = useMemo(() => validateInquiryForm(form, serviceKey), [form, serviceKey]);
  const visibleErrors = hasTriedSubmit ? { ...serverFieldErrors, ...clientFieldErrors } : {};

  const price = useMemo(
    () =>
      calculateInquiryPrice({
        serviceKey,
        tier: selectedTier,
        sitePrices,
        passengerCount: getPassengerCount(form),
        serviceTime: form[serviceConfig.serviceTimeField],
      }),
    [serviceKey, selectedTier, sitePrices, form, serviceConfig.serviceTimeField]
  );

  // A server complaint is about what was sent; once the guest edits, drop it.
  useEffect(() => setServerFieldErrors({}), [form]);

  // Do not let a request finish into an unmounted page.
  useEffect(() => () => submitCancelSourceRef.current && submitCancelSourceRef.current.cancel(), []);

  const handleSelectGroup = (group) => {
    setActiveGroup(group);
    // Station has no Connecting Flights tab, so fall back to Arrival.
    if (!JOURNEYS_BY_GROUP[group].includes(activeJourney)) setActiveJourney(JOURNEY_ARRIVAL);
  };

  const handleSubmitSuccess = () => {
    resetForm();
    setHasTriedSubmit(false);
    setIsSuccessModalOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitFailure = (error) => {
    if (error.messageKey === "Errors.Cancelled") return;
    setServerFieldErrors(mapServerFieldErrors(error.fieldErrors));
    setSubmitErrorKey(error.messageKey || "Errors.Server");
    console.error("Meet & Assist inquiry could not be sent", {
      serviceKey,
      status: error.status,
      messageKey: error.messageKey,
    });
    if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) scrollToFirstInvalidField();
  };

  const handleInquirySubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setHasTriedSubmit(true);
    setSubmitErrorKey("");

    if (Object.keys(clientFieldErrors).length > 0) {
      setSubmitErrorKey("Errors.CheckFields");
      scrollToFirstInvalidField();
      return;
    }

    const payload = buildInquiryPayload({
      form,
      serviceKey,
      estimatedTotal: price.total,
      language,
      translateToEnglish: i18n.getFixedT("en", "translation", "meetAssistInquiry"),
    });

    submitCancelSourceRef.current = createCancelSource();
    setIsSubmitting(true);
    try {
      await submitMeetAssistInquiry(payload, submitCancelSourceRef.current.token);
      handleSubmitSuccess();
    } catch (error) {
      handleSubmitFailure(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FrontLayout>
      <div className="tw:max-w-[1920px] tw:mx-auto tw:bg-[#F9F9F9]">
        <MeetAssistHero />
        <GroupTabs activeGroup={activeGroup} onSelectGroup={handleSelectGroup} />

        <form
          noValidate
          onSubmit={handleInquirySubmit}
          className="tw:max-w-[1440px] tw:mx-auto tw:px-0 tw:lg:px-[70px] tw:pt-8 tw:lg:pt-[54px] tw:pb-10 tw:lg:pb-[88px] tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-start tw:gap-[5px] tw:lg:gap-[22px]"
        >
          <div className="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:gap-y-[5px] tw:lg:gap-y-2">
            <Reveal onMount y={20} delay={0.1}>
              <JourneyTabs group={activeGroup} activeJourney={activeJourney} onSelectJourney={setActiveJourney} />
              <FormCard
                shouldReveal={false}
                title={t("meetAssistInquiry.Sections.Details")}
                className="tw:!rounded-t-none tw:!border-t-0"
              >
                {/* Switching tabs slides the old fields out and the new ones in,
                    while the card's height eases to fit. */}
                <SwapPanel activeKey={serviceKey} direction={slideDirection}>
                  <div className="tw:flex tw:flex-col tw:gap-y-7">
                    <MeetAssistDetailsSection
                      serviceKey={serviceKey}
                      form={form}
                      errors={visibleErrors}
                      sitePrices={sitePrices}
                      selectedTier={selectedTier}
                      updateField={updateField}
                      actions={actions}
                    />
                  </div>
                </SwapPanel>
              </FormCard>
            </Reveal>

            <PassengerInformationSection
              group={activeGroup}
              selectedTier={selectedTier}
              form={form}
              errors={visibleErrors}
              updateField={updateField}
            />
            {/* Connecting flights have no car to meet, so this card folds away. */}
            <Fold isOpen={serviceConfig.hasCarAndDriver}>
              <CarDriverSection form={form} errors={visibleErrors} updateField={updateField} />
            </Fold>
            <ContactSection form={form} errors={visibleErrors} updateField={updateField} />
          </div>

          {/* Not sticky: a site-wide wrapper sets overflow-x: hidden, which
              stops position: sticky from working anywhere inside it. */}
          <Reveal
            as="aside"
            onMount
            y={20}
            delay={0.2}
            className="tw:w-full tw:lg:w-[340px] tw:shrink-0 tw:bg-white tw:lg:bg-transparent tw:px-5 tw:lg:px-0 tw:py-[30px] tw:lg:py-0 tw:flex tw:flex-col tw:gap-y-8">
            <div className="tw:flex tw:flex-col tw:gap-y-[6px] tw:lg:gap-y-3">
              <SummaryCard serviceKey={serviceKey} selectedTier={selectedTier} form={form} />
              <TotalCard serviceKey={serviceKey} selectedTier={selectedTier} price={price} />
              <CancellationPolicyLink />
            </div>
            <SubmitPanel isSubmitting={isSubmitting} submitErrorKey={submitErrorKey} />
          </Reveal>
        </form>
      </div>

      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        title={t("meetAssistInquiry.Success.Title")}
        message={t("meetAssistInquiry.Success.Message")}
      />
    </FrontLayout>
  );
};

export default MeetAssistInquiryPage;
